/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import type { EuiContextMenuPanelItemDescriptor } from '@elastic/eui'
import { EuiButtonIcon, EuiContextMenu, EuiPopover, useGeneratedHtmlId } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { withErrorBoundary } from '@modules/cui/Boundary'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isConsumptionProcessing } from '@modules/billing-lib/billingDetails/utils'

import DeprovisionFlyout from './DeprovisionFlyout'
import ChangeDomainFlyout from './ChangeDomainFlyout'

import type { FunctionComponent } from 'react'

interface Props {
  organizationId: string
}

const Settings: FunctionComponent<Props> = ({ organizationId }) => {
  const [isPopoverOpen, setPopover] = useState(false)
  const [isDeprovisionFlyoutOpen, setDeprovisionFlyoutOpen] = useState(false)
  const [isChangeDomainFlyoutOpen, setChangeDomainFlyoutOpen] = useState(false)
  const billingDetails = useBillingDetails()

  const settingsMenuPopoverId = useGeneratedHtmlId({
    prefix: 'settingsMenuPopover',
  })

  const onButtonClick = () => {
    setPopover(!isPopoverOpen)
  }

  const closePopover = () => {
    setPopover(false)
  }

  const getSettings = (): EuiContextMenuPanelItemDescriptor[] => {
    const settings: EuiContextMenuPanelItemDescriptor[] = []

    if (billingDetails.data && isConsumptionProcessing(billingDetails.data)) {
      settings.push({
        name: (
          <FormattedMessage
            id='billing-details.settings.deprovision'
            defaultMessage='Deprovision'
          />
        ),
        onClick: () => {
          setDeprovisionFlyoutOpen(true)
          closePopover()
        },
      })
    }

    // Add Change Domain option for direct organizations
    if (billingDetails.data && billingDetails.data.channel !== 'heroku') {
      settings.push({
        name: (
          <FormattedMessage
            id='billing-details.settings.change-domain'
            defaultMessage='Change Domain'
          />
        ),
        onClick: () => {
          setChangeDomainFlyoutOpen(true)
          closePopover()
        },
      })
    }

    if (settings.length === 0) {
      settings.push({
        name: (
          <FormattedMessage
            id='billing-details.settings-not-available'
            defaultMessage='Settings unavailable'
          />
        ),
        icon: 'warning',
        onClick: () => {
          closePopover()
        },
      })
    }

    return settings
  }

  return (
    <Fragment>
      <EuiPopover
        id={settingsMenuPopoverId}
        button={
          <EuiButtonIcon
            iconType='gear'
            aria-label='Settings'
            onClick={onButtonClick}
            data-test-id='settings-button'
          />
        }
        isOpen={isPopoverOpen}
        closePopover={closePopover}
        panelPaddingSize='none'
        anchorPosition='downLeft'
        data-test-id='settings-popover'
      >
        <EuiContextMenu
          initialPanelId={0}
          panels={[
            {
              id: 0,
              title: (
                <FormattedMessage id='billing-details.settings-title' defaultMessage='Settings' />
              ),
              items: getSettings(),
            },
          ]}
        />
      </EuiPopover>
      {isDeprovisionFlyoutOpen && (
        <DeprovisionFlyout
          organizationId={organizationId}
          onClose={() => setDeprovisionFlyoutOpen(false)}
        />
      )}
      {isChangeDomainFlyoutOpen && (
        <ChangeDomainFlyout
          organizationId={organizationId}
          onClose={() => setChangeDomainFlyoutOpen(false)}
        />
      )}
    </Fragment>
  )
}

export default withErrorBoundary(Settings)
